import clsx from 'clsx';
import { isEmpty, isUndefined } from 'lodash-es';
import {
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useRef,
} from 'react';

import { ContentGrid } from './components/ContentGrid.js';
import { Label } from './components/Label.js';
import {
  SingleValueToolbar,
  type SingleValueToolbarConfig,
} from './components/SingleValueToolbar.js';
import { Trend } from './components/trend/Trend.js';
import type { TrendProps } from './components/trend/types/trend-props.js';
import { Value } from './components/Value.js';
import {
  SingleValueContext,
  type SingleValueContextProps,
} from './contexts/single-value.context.js';
import { FontSizeProvider } from './providers/font-size.provider.js';
import {
  chartStateHandler,
  singleValueContainer,
  singleValueInnerContainer,
  sparklineArea,
  trendArea,
} from './SingleValueRenderer.sty.js';
import type {
  PropsFromGrid,
  SingleValueBaseProps,
} from './types/single-value-base-props.js';
import { buildValueContentGridRows } from './utils/build-value-content-grid-rows.js';
import { formatLabel } from './utils/format-label.js';
import { formatValue } from './utils/format-value.js';
import { getCalculatedColor } from './utils/get-calculated-color.js';
import { getContentAndBackgroundColor } from './utils/get-content-and-background-color.js';
import { useContainerSize } from '../../../../core/hooks/useContainerSize.js';
import { defaultChartSize } from '../../../../core/styles/defaultChartValues.sty.js';
import { ColorRuleProps } from '../../../core/types/color-rule.js';
import { ChartStateHandler } from '../chart-states/ChartStateHandler.js';
import { LoaderOverlay } from '../loader-overlay/LoaderOverlay.js';
import { SparklineDisplay } from '../sparkline-display/SparklineDisplay.js';
import type { SparklineDisplayProps } from '../sparkline-display/types/sparkline-display-props.js';

type SingleValueRendererProps = PropsWithChildren<SingleValueBaseProps> & {
  dataItem?: Record<string, unknown>;
  sparklineProps?: SparklineDisplayProps;
  sparklineChildren?: ReactNode;
  trendProps?: TrendProps;
  colorRules?: ColorRuleProps[];
  loading?: boolean;
  emptyState?: ReactNode;
  errorState?: ReactNode | ((errorMessage: string) => ReactElement);
  toolbar?: SingleValueToolbarConfig;
  propsFromGrid?: PropsFromGrid;
};

export const SingleValueRenderer = (props: SingleValueRendererProps) => {
  const {
    data,
    formatter,
    unit,
    prefixIcon,
    alignment,
    label,
    color,
    colorRules = [],
    emptyState,
    errorState,
    loading = false,
    sparklineProps,
    sparklineChildren,
    trendProps,
    applyThresholdBackground,
    propsFromGrid,
    toolbar,
    dataItem,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const { height: containerHeight } = useContainerSize(containerRef);
  const { color: calculatedColor, ruleApplied } = getCalculatedColor(
    data,
    colorRules,
    color,
    dataItem,
  );

  const applyBackgroundColor = applyThresholdBackground && ruleApplied;

  const { contentColor, backgroundColor } = getContentAndBackgroundColor(
    calculatedColor,
    applyBackgroundColor,
  );

  const { value, unit: formatterUnit } = formatValue(data, unit, formatter);

  const formattedLabel = formatLabel(label);

  const context: SingleValueContextProps = {
    value,
    unit: formatterUnit ?? '',
    label: formattedLabel ?? '',
    prefixIcon,
    alignment: alignment,
  };

  const { innerGrid, outerGrid } = buildValueContentGridRows(
    !isUndefined(label),
    !isUndefined(data),
    !isUndefined(trendProps),
    !isUndefined(sparklineProps),
  );

  const getSparklineColor = () =>
    applyBackgroundColor ? contentColor : sparklineProps?.color ?? contentColor;

  return (
    <div
      className={singleValueContainer}
      style={{ backgroundColor }}
      ref={containerRef}
    >
      <FontSizeProvider
        text={context.prefixIcon ? `00${context.value}` : `${context.value}`}
        suffix={context.unit}
      >
        <SingleValueContext.Provider value={context}>
          <ContentGrid color={contentColor} gridTemplate={outerGrid}>
            {label !== undefined ? (
              <Label color={applyBackgroundColor ? contentColor : undefined} />
            ) : null}
            <div className={clsx(singleValueInnerContainer)}>
              <ChartStateHandler
                empty={isEmpty(value)}
                height="100cqh"
                emptyState={emptyState}
                errorState={errorState}
                isLoading={loading}
                style={{
                  gridTemplate: innerGrid,
                }}
                className={clsx(defaultChartSize, chartStateHandler)}
              >
                <Value />

                {!isUndefined(trendProps) ? (
                  <div className={trendArea}>
                    <Trend
                      {...trendProps}
                      contrastColor={
                        applyBackgroundColor ? contentColor : undefined
                      }
                    />
                  </div>
                ) : null}

                {!isUndefined(sparklineProps) ? (
                  <div className={sparklineArea}>
                    <SparklineDisplay
                      {...sparklineProps}
                      color={getSparklineColor()}
                      labelsAlignment="right"
                      labelsFixedWidth={undefined}
                      ticksColor={
                        applyBackgroundColor ? contentColor : undefined
                      }
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {sparklineChildren}
                    </SparklineDisplay>
                  </div>
                ) : null}

                {loading && <LoaderOverlay />}
              </ChartStateHandler>
            </div>
          </ContentGrid>
          <SingleValueToolbar
            {...(propsFromGrid?.toolbar
              ? propsFromGrid?.toolbar
              : (toolbar as SingleValueToolbarConfig))}
            parentHeight={containerHeight}
            intentsAsyncTemplate={propsFromGrid?.intentsAsyncTemplate}
          />
        </SingleValueContext.Provider>
      </FontSizeProvider>
    </div>
  );
};
